import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Shield, Upload, Copy, CheckCircle2, AlertTriangle, ChevronLeft, Send } from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { api, formatApiError } from "../services/api";

/**
 * EscrowDeposit (V3.17)
 * ─────────────────────
 * Page « Sécurisation des fonds AFRICTAL » pour l'entreprise après création
 * d'un escrow. Choix 100 % ou 50/50 lors de la création (ici on affiche les
 * instructions MoMo pour l'installment en attente).
 *
 * URL : /escrow/:id/deposit
 */
export default function EscrowDeposit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [esc, setEsc] = useState(null);
  const [channels, setChannels] = useState([]);
  const [helpText, setHelpText] = useState("");
  const [selectedChannel, setSelectedChannel] = useState("");
  const [proofUrl, setProofUrl] = useState("");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [installment, setInstallment] = useState(1);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const [e, c] = await Promise.all([
        api.get(`/v1/escrow/${id}`),
        api.get("/v1/escrow/deposit-channels"),
      ]);
      setEsc(e.data.escrow);
      setChannels((c.data.channels || []).filter((x) => x.active));
      setHelpText(c.data.help_text || "");
      const insts = e.data.escrow.installments || [];
      const pending = insts.findIndex((x) => x.status === "pending" || x.status === "rejected");
      if (pending >= 0) setInstallment(pending + 1);
    } catch (err) {
      const msg = formatApiError(err);
      setLoadError(msg);
      toast.error(msg);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const copy = (text) => {
    navigator.clipboard.writeText(text || "");
    toast.success("Copié");
  };

  const uploadProof = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/v1/escrow/${id}/proof`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProofUrl(data.url);
      toast.success("Preuve téléversée");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!selectedChannel) return toast.error("Choisissez un canal Mobile Money");
    if (!proofUrl) return toast.error("La preuve de paiement est obligatoire");
    setSubmitting(true);
    try {
      await api.post(`/v1/escrow/${id}/declare-payment`, {
        channel_code: selectedChannel,
        proof_url: proofUrl,
        reference: reference || undefined,
        note: note || undefined,
        installment,
      });
      toast.success("Déclaration transmise à AFRICTAL pour vérification");
      navigate(`/escrow/${id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!esc) {
    if (loadError) {
      return (
        <PrivateLayout title="Sécurisation des fonds AFRICTAL">
          <div className="afr-empty" data-testid="escrow-deposit-error">
            <div className="afr-empty__title">Impossible de charger ce séquestre</div>
            <div style={{ marginTop: 6, color: "#64748b" }}>{loadError}</div>
            <Link to="/escrow" className="afr-btn afr-btn--outline afr-btn--sm" style={{ marginTop: 12 }}>
              <ChevronLeft size={12} /> Retour à la liste
            </Link>
          </div>
        </PrivateLayout>
      );
    }
    return <PrivateLayout title="Sécurisation des fonds AFRICTAL"><div data-testid="escrow-deposit-loading">Chargement…</div></PrivateLayout>;
  }

  const currentInst = (esc.installments || [])[installment - 1] || {};
  const expected = currentInst.expected || 0;
  const alreadyDeclared = currentInst.status === "declared";
  const alreadyVerified = currentInst.status === "verified";

  return (
    <PrivateLayout
      title="🛡️ Sécurisation des fonds AFRICTAL"
      breadcrumbs={[{ to: "/escrow", label: "Séquestres" }, { label: "Dépôt" }]}
      actions={<Link to={`/escrow/${id}`} className="afr-btn afr-btn--outline afr-btn--sm"><ChevronLeft size={14} /> Retour</Link>}
    >
      <div style={{ maxWidth: 820 }} data-testid="escrow-deposit-page">
        {/* Bandeau de réassurance */}
        <section
          data-testid="escrow-reassurance-banner"
          style={{
            padding: 18, background: "linear-gradient(135deg,#065F46 0%,#009640 100%)",
            color: "#fff", borderRadius: 10, marginBottom: 18,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <Shield size={28} />
            <div style={{ fontSize: "1.2rem", fontWeight: 700 }}>Vos fonds sont protégés par AFRICTAL</div>
          </div>
          <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.7, fontSize: "0.9rem" }}>
            <li>Le talent ne sera payé qu'<strong>après validation</strong> de la mission par vos soins.</li>
            <li>Vos fonds sont bloqués en toute sécurité sur le compte séquestre AFRICTAL.</li>
            <li>AFRICTAL <strong>arbitre gratuitement</strong> tout litige entre vous et le talent.</li>
          </ul>
        </section>

        {/* Résumé */}
        <section className="afr-card" style={{ padding: 16, marginBottom: 16 }} data-testid="escrow-summary">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12 }}>
            <Kv label="Escrow" value={`#${esc.id.slice(0, 8)}`} />
            <Kv label="Montant total" value={`${esc.amount.toLocaleString()} ${esc.currency}`} testid="escrow-amount" />
            <Kv label="Plan de dépôt" value={esc.deposit_plan === "full" ? "100 % avant démarrage" : "50 % + 50 %"} />
            <Kv label="Commission AFRICTAL" value={`${(esc.commission_rate * 100).toFixed(0)} %`} />
            <Kv label="Talent recevra (net)" value={`${esc.net_amount.toLocaleString()} ${esc.currency}`} />
            <Kv label="Statut actuel" value={esc.status} testid="escrow-status-value" />
          </div>
        </section>

        {(alreadyVerified) && (
          <div data-testid="escrow-installment-verified" className="afr-card"
               style={{ padding: 12, background: "#DCFCE7", color: "#065F46", marginBottom: 16 }}>
            <CheckCircle2 style={{ verticalAlign: "middle" }} /> Ce versement a déjà été vérifié par AFRICTAL.
          </div>
        )}

        {(alreadyDeclared && !alreadyVerified) && (
          <div data-testid="escrow-installment-declared" className="afr-card"
               style={{ padding: 12, background: "#FEF3C7", color: "#78350F", marginBottom: 16 }}>
            <AlertTriangle style={{ verticalAlign: "middle" }} /> Votre déclaration est en cours de vérification par AFRICTAL.
          </div>
        )}

        {!alreadyDeclared && !alreadyVerified && (
          <>
            {/* Sélection canal */}
            <section className="afr-card" style={{ padding: 16, marginBottom: 16 }} data-testid="escrow-channels-card">
              <h3 style={{ margin: "0 0 12px", fontSize: "1rem" }}>1. Choisissez votre canal Mobile Money</h3>
              {channels.length === 0 ? (
                <div style={{ padding: 10, background: "#FEE2E2", borderRadius: 6, color: "#991B1B" }}
                     data-testid="escrow-no-channels">
                  Aucun canal Mobile Money n'est configuré actuellement. Contactez l'administrateur AFRICTAL.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 10 }}>
                  {channels.map((c) => (
                    <div
                      key={c.code}
                      onClick={() => setSelectedChannel(c.code)}
                      data-testid={`escrow-channel-${c.code}`}
                      style={{
                        padding: 12, border: `2px solid ${selectedChannel === c.code ? "#009640" : "#E2E8F0"}`,
                        borderRadius: 8, cursor: "pointer", background: selectedChannel === c.code ? "rgba(0,150,64,0.06)" : "#fff",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <div style={{ fontWeight: 700 }}>{c.label}</div>
                          <div style={{ fontSize: "0.85rem", color: "#475569" }}>
                            {c.holder_name && <>Bénéficiaire : <strong>{c.holder_name}</strong> · </>}
                            Numéro : <strong>{c.number}</strong>
                          </div>
                          {c.instructions && <div style={{ fontSize: "0.78rem", color: "#64748b", marginTop: 4 }}>{c.instructions}</div>}
                        </div>
                        <button
                          type="button"
                          className="afr-btn afr-btn--outline afr-btn--sm"
                          onClick={(e) => { e.stopPropagation(); copy(c.number); }}
                          data-testid={`escrow-copy-${c.code}`}
                        >
                          <Copy size={12} /> Copier
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ marginTop: 12, padding: 10, background: "#F1F5F9", borderRadius: 6, fontSize: "0.82rem" }}>
                <strong>Montant à envoyer : {expected.toLocaleString()} {esc.currency}</strong> · Référence à indiquer : <strong>#{esc.id.slice(0, 8)}</strong>
                {helpText && <div style={{ marginTop: 6, color: "#64748b" }}>{helpText}</div>}
              </div>
            </section>

            {/* Preuve */}
            <section className="afr-card" style={{ padding: 16, marginBottom: 16 }} data-testid="escrow-proof-card">
              <h3 style={{ margin: "0 0 12px", fontSize: "1rem" }}>2. Joignez la preuve de paiement (obligatoire)</h3>
              <input
                type="file"
                accept=".png,.jpg,.jpeg,.webp,.pdf"
                onChange={(e) => uploadProof(e.target.files?.[0])}
                data-testid="escrow-proof-input"
              />
              {uploading && <div style={{ marginTop: 8, fontSize: "0.85rem", color: "#64748b" }}>Téléversement…</div>}
              {proofUrl && (
                <div style={{ marginTop: 10, padding: 8, background: "#DCFCE7", borderRadius: 6, fontSize: "0.85rem", color: "#065F46" }}
                     data-testid="escrow-proof-ready">
                  <CheckCircle2 size={14} style={{ verticalAlign: "middle" }} /> Preuve prête à être envoyée
                </div>
              )}
              <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
                <input value={reference} onChange={(e) => setReference(e.target.value)}
                       placeholder="Référence de la transaction MoMo (optionnel)"
                       data-testid="escrow-reference-input"
                       style={inputStyle} />
                <textarea value={note} onChange={(e) => setNote(e.target.value)}
                          rows={2}
                          placeholder="Note pour l'admin (optionnel)"
                          data-testid="escrow-note-input"
                          style={{ ...inputStyle, resize: "vertical" }} />
              </div>
            </section>

            {/* Submit */}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                className="afr-btn afr-btn--primary"
                onClick={submit}
                disabled={submitting || !selectedChannel || !proofUrl}
                data-testid="escrow-declare-submit"
              >
                {submitting ? "Envoi…" : <><Send size={14} /> J'ai envoyé les fonds</>}
              </button>
            </div>
          </>
        )}
      </div>
    </PrivateLayout>
  );
}

function Kv({ label, value, testid }) {
  return (
    <div>
      <div style={{ fontSize: "0.72rem", color: "#94a3b8", textTransform: "uppercase" }}>{label}</div>
      <div data-testid={testid} style={{ fontWeight: 700, fontSize: "0.95rem" }}>{value}</div>
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "8px 10px", border: "1px solid #E2E8F0", borderRadius: 6, fontSize: "0.9rem",
};
